/**
 * Service-record observation helpers.
 *
 * Extracted from VehicleServiceRecordDetail.jsx so the workshop and the fleet
 * portal share one implementation of the IN/OUT pairing logic. The pairing is
 * subtle enough (see the composite-key note below) that cloning it would be a
 * good way to introduce a silent bug in one copy only.
 *
 * These are pure functions — no API calls, no React. The two detail components
 * differ in how they resolve image URLs (the workshop asks the server to presign
 * an arbitrary S3 key; the fleet portal receives presigned URLs inline), so they
 * remain separate components on top of this shared core.
 */

/**
 * Pair IN and OUT observation lists by composite key
 * (measurementUuid, positionLabel, subPositionLabel).
 *
 * Iterates the IN list first to preserve the order the technician originally
 * recorded measurements; OUT entries are looked up by composite key and attached
 * to their matching IN row. Any OUT entries with no IN counterpart are appended
 * at the end with a null `in` field, so nothing gets silently dropped — those
 * shouldn't exist in normal operation but we surface them rather than hide them.
 *
 * The triple key is required for sub-positioned measurements (e.g. tyre tread
 * depth at LF×{OUTER,MIDDLE,INNER}): without subPositionLabel in the key, all
 * three LF rows would collide on `measurementUuid::LF` and only one would
 * survive. With it, each sub-cell gets its own pair, and the IN-list ordering
 * naturally groups them (LF OUTER, LF MIDDLE, LF INNER, RF OUTER, ...).
 *
 * Returns an array of:
 *   { measurementUuid, name, positionLabel, subPositionLabel, in, out }
 *
 * where `in` and `out` are the raw observation objects (or null if missing).
 */
export const pairObservations = (inList, outList) => {
    const safeIn  = Array.isArray(inList)  ? inList  : [];
    const safeOut = Array.isArray(outList) ? outList : [];

    const keyOf = (obs) =>
        `${obs.measurementUuid}::${obs.positionLabel ?? ''}::${obs.subPositionLabel ?? ''}`;

    const outByKey = new Map();
    for (const obs of safeOut) outByKey.set(keyOf(obs), obs);

    const pairs = [];
    const consumedKeys = new Set();

    for (const inObs of safeIn) {
        const key = keyOf(inObs);
        const outObs = outByKey.get(key) || null;
        if (outObs) consumedKeys.add(key);
        pairs.push({
            measurementUuid:  inObs.measurementUuid,
            name:             inObs.name,
            positionLabel:    inObs.positionLabel,
            subPositionLabel: inObs.subPositionLabel,
            in:               inObs,
            out:              outObs,
        });
    }

    // Surface any OUT-only entries (no IN counterpart) — defensive; shouldn't happen.
    for (const outObs of safeOut) {
        const key = keyOf(outObs);
        if (consumedKeys.has(key)) continue;
        pairs.push({
            measurementUuid:  outObs.measurementUuid,
            name:             outObs.name,
            positionLabel:    outObs.positionLabel,
            subPositionLabel: outObs.subPositionLabel,
            in:               null,
            out:              outObs,
        });
    }

    return pairs;
};

/**
 * Reshape a flat paired list into a nested hierarchy:
 *
 *   [
 *     {
 *       measurementUuid, name,
 *       positions: [
 *         { positionLabel, pairs: [pair, pair, ...] },
 *         ...
 *       ]
 *     },
 *     ...
 *   ]
 *
 * Displays observations grouped first by measurement (one name header) and then
 * by position (one chip per corner / side), with sub-position rows underneath.
 *
 * For single-slot measurements (no positionLabel and no subPositionLabel) the
 * shape still works — the measurement gets one position group with
 * `positionLabel = null` containing one pair. Renderers hide the position header
 * when `positionLabel` is falsy, so the row sits directly under the measurement
 * name with no decorative grouping.
 *
 * Ordering is preserved from the input list, which itself follows the IN-list
 * insertion order — i.e. the technician's recording sequence.
 */
export const groupPairsByPosition = (pairs) => {
    const measurementMap = new Map();

    for (const pair of pairs) {
        let mEntry = measurementMap.get(pair.measurementUuid);
        if (!mEntry) {
            mEntry = {
                measurementUuid: pair.measurementUuid,
                name:            pair.name,
                positionMap:     new Map(),
            };
            measurementMap.set(pair.measurementUuid, mEntry);
        }

        const posKey = pair.positionLabel ?? '';
        let pEntry = mEntry.positionMap.get(posKey);
        if (!pEntry) {
            pEntry = { positionLabel: pair.positionLabel, pairs: [] };
            mEntry.positionMap.set(posKey, pEntry);
        }
        pEntry.pairs.push(pair);
    }

    return Array.from(measurementMap.values()).map((m) => ({
        measurementUuid: m.measurementUuid,
        name:            m.name,
        positions:       Array.from(m.positionMap.values()),
    }));
};

/**
 * Convenience: go straight from a work item to the grouped observation tree.
 * Returns an empty array when the work item has no measurements at all.
 */
export const groupedObservationsFor = (workItem) => {
    const inList  = workItem?.measurementsObserved;
    const outList = workItem?.measurementsObservedOut;

    if (!inList?.length && !outList?.length) return [];

    return groupPairsByPosition(pairObservations(inList, outList));
};

/**
 * Tailwind classes for an observation's RED / AMBER / GREEN band.
 * Matches the workshop's palette so a fleet customer sees the same colours their
 * workshop does.
 */
export const OBSERVATION_STATUS_STYLES = {
    GREEN:        'bg-emerald-50 text-emerald-800 border-emerald-200',
    AMBER:        'bg-amber-50 text-amber-800 border-amber-200',
    RED:          'bg-red-50 text-red-800 border-red-200',
    UNCLASSIFIED: 'bg-slate-50 text-slate-600 border-slate-200',
};